/**
 * nameList — render a list of people's names under a length budget WITHOUT
 * hiding how many there are.
 *
 * 2026-07-30 (found by inspection on a live trust): the Stakeholders card
 * header read "5 records" while the row beneath listed three names and simply
 * stopped — `names.slice(0, 3).join(' · ')`. The count was the truth; the list
 * was silently truncated, so the card read as if the register held three
 * people. Same class, worse, on the trustee row: it showed the FIRST name only,
 * whatever the count.
 *
 * The rule here: show up to `max` names, then DECLARE the remainder ("+2
 * more"). A row that fits says nothing extra. Nothing on a fiduciary record
 * should imply a shorter register than the one we hold.
 */

/** How many names a stakeholder row shows before it starts counting. */
export const DEFAULT_NAMES_SHOWN = 3;

/**
 * "Ada Lovelace · Grace Hopper · Joan Clarke +2 more" — the first `max` real
 * names, middle-dot separated, followed by a declared count of the ones left
 * out. Blank/missing entries are dropped (never rendered as a gap) and do not
 * count towards the remainder. No names at all → 'Not recorded'.
 */
export function nameList(
  names: ReadonlyArray<string | null | undefined>,
  max: number = DEFAULT_NAMES_SHOWN,
): string {
  const real = names
    .map((n) => (typeof n === 'string' ? n.trim() : ''))
    .filter((n) => n.length > 0);
  if (real.length === 0) return 'Not recorded';
  const shown = real.slice(0, Math.max(1, max));
  const line = shown.join(' · ');
  const hidden = real.length - shown.length;
  return hidden > 0 ? `${line} +${hidden} more` : line;
}
